/**
 * Formatage & petites fonctions pures partagées candidat/admin. Zéro
 * dépendance React — testable à la main, importable partout dans
 * `regie-test/`.
 */
import { DAY_LABELS, SLOT_LABELS } from "./constants";
import type { Availability, CoverageMatrix, DayKey, SlotKey } from "./types";
import { DAY_KEYS, SLOT_KEYS } from "./types";

/** "40 €" / "39,90 €" — pas de décimales inutiles. */
export function formatEuros(cents: number): string {
  const value = cents / 100;
  const hasCents = !Number.isInteger(value);
  return `${value.toLocaleString("fr-FR", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })} €`;
}

/** Durée courte pour un DELTA de carte (quelques secondes à quelques
 *  minutes) : "9 s", "42 s", "2 min". */
export function formatDwellShort(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)} s`;
  return `${Math.round(ms / 60_000)} min`;
}

/** Durée pour un agrégat (médiane sur tout un test) — toujours en minutes,
 *  même en dessous de 60s (évite un "0 min" trompeur en dessous de 30s en
 *  arrondissant à 1 min minimum une fois qu'on dépasse quelques secondes). */
export function formatDwellMinutes(ms: number): string {
  const minutes = ms / 60_000;
  if (minutes < 1) return `${Math.round(ms / 1000)} s`;
  return `${Math.round(minutes)} min`;
}

/** Fraction 0..1 → "80 %". Défensif : si on reçoit déjà un pourcentage
 *  (>1), on ne multiplie pas deux fois. */
export function formatPercent(fraction: number): string {
  const pct = fraction <= 1 ? fraction * 100 : fraction;
  return `${Math.round(pct)} %`;
}

/** ISO → "11/07" (jj/mm), fuseau du navigateur — suffisant pour une colonne
 *  "Créé le" d'admin. */
export function formatDateFr(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" }).format(d);
}

/** ISO → "11/07/2026" complet (fiches détail). */
export function formatDateFrLong(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

/** Compacte une grille de dispos en "Soir ×5 · Ap-m ×3" (tri décroissant,
 *  créneaux vides omis). Utilisé pour le résumé du formulaire candidat AVANT
 *  envoi (le serveur calcule le sien pour l'admin, cf. `formatAvailabilitySummaryUnknown`). */
export function summarizeAvailability(av: Availability): string {
  const counts: { slot: SlotKey; count: number }[] = SLOT_KEYS.map((slot) => ({
    slot,
    count: DAY_KEYS.reduce((n, day) => n + (av[day]?.includes(slot) ? 1 : 0), 0),
  })).filter((x) => x.count > 0);
  if (counts.length === 0) return "Aucun créneau coché";
  counts.sort((a, b) => b.count - a.count);
  return counts
    .map((c) => `${SLOT_LABELS[c.slot].label} ×${c.count}`)
    .join(" · ");
}

/**
 * `availability_summary` (admin, GET /candidates) n'a pas de forme figée
 * dans le contrat — soit une chaîne déjà prête côté serveur, soit une map
 * jour→créneau→compte. On gère les deux défensivement plutôt que de
 * planter sur une hypothèse fausse.
 */
export function formatAvailabilitySummaryUnknown(value: unknown): string {
  if (typeof value === "string") return value || "—";
  if (value && typeof value === "object") {
    // Tente une forme { a: n, b: n, …, f: n } (shifts A–F)
    const rec = value as Record<string, unknown>;
    const counts = SLOT_KEYS.map((slot) => ({
      slot,
      count: typeof rec[slot] === "number" ? (rec[slot] as number) : 0,
    })).filter((x) => x.count > 0);
    if (counts.length > 0) {
      counts.sort((a, b) => b.count - a.count);
      return counts.map((c) => `${SLOT_LABELS[c.slot].label} ×${c.count}`).join(" · ");
    }
  }
  return "—";
}

/** Libellé FR court d'un geste — utilisé dans l'admin (attendu vs fait). */
export function gestureLabel(gesture: string | null): string {
  switch (gesture) {
    case "send":
      return "Envoyer";
    case "correct":
      return "Corriger";
    case "discard":
      return "Écarter";
    case "reorient":
      return "Réorienter";
    default:
      return "—";
  }
}

export function dayLabel(day: DayKey): string {
  return DAY_LABELS[day];
}

/** Statut technique candidat → chip FR (Envoyé / Ouvert / Terminé), même
 *  vocabulaire que la maquette admin (onglet Liens candidats). */
export function statusLabel(status: string): string {
  switch (status) {
    case "created":
      return "Envoyé";
    case "started":
      return "Ouvert";
    case "submitted":
      return "Terminé";
    default:
      return status;
  }
}

/** Horodatage epoch ms → ISO (helper trivial, isolé pour rester testable /
 *  remplaçable sans toucher les call-sites). */
export function nowIso(): string {
  return new Date().toISOString();
}

/** Médiane d'une liste de nombres — `null` si vide. */
export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Durée "3 min 40" (minutes + secondes, secondes omises si nulles) — pour la
 *  durée de l'épreuve de vente sur la fiche admin. (Le bloc « Rythme » qu'elle
 *  servait est parti avec le test de cartes de La Régie, le 19-08.) */
export function formatDurationMinSec(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec} s`;
  return sec === 0 ? `${min} min` : `${min} min ${sec}`;
}

/** Nombre de cases (jour × créneau) sans aucun candidat disponible — pilote
 *  le KPI "Créneaux non couverts" et le rouge de la grille de couverture. */
export function countCoverageGaps(matrix: CoverageMatrix): number {
  let gaps = 0;
  for (const day of DAY_KEYS) {
    for (const slot of SLOT_KEYS) {
      if (!matrix[day]?.[slot]?.length) gaps += 1;
    }
  }
  return gaps;
}
